import { EventType, ParsedRouteDefinition } from '../../interfaces';

/**
 * This takes a reference to the google map and puts polygons there. It also returns
 * a list of said polygons so that they can be removed later.
 *
 * @param map Google maps object
 * @param kml Parsed KML definition
 * @returns
 */
export const drawKMLonMap = (map: google.maps.Map, kml: ParsedRouteDefinition, eventTypes: EventType[]): google.maps.Polyline[] => {
  if (!kml) {
    return;
  }

  const { polygons } = kml;
  const paths: google.maps.Polyline[] = [];

  polygons.forEach((polygon) => {
    const { path, color, type } = polygon;

    if (eventTypes?.length > 0 && !eventTypes.includes(type as EventType) && !eventTypes.includes((type + '_MATCH') as EventType)) {
      return;
    }

    const polyline = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: color ?? '#00EE00',
      strokeOpacity: 1.0,
      strokeWeight: 2,
    });

    polyline.setMap(map);
    paths.push(polyline);
  });

  return paths;
};

/**
 * Remove unused objects from the map to prevent memory leaks and make sure we don't clog
 * the renderer with unnecessary objects.
 */
export const clearFromMap = (objects: (google.maps.Polyline | google.maps.Marker)[], map: google.maps.Map) => {
  (objects || []).forEach((object) => {
    object.setMap(null);
  });
};

/**
 * Street view doesn't allow drawing polygons, so we'll just draw many points instead:
 * we'll draw them directly on the map, and return a reference to them so that they can
 * be removed later.
 */
export const drawKMLPolygonsAsPointsonMap = (
  map: google.maps.Map,
  kml: ParsedRouteDefinition,
  center: google.maps.LatLngLiteral,
  eventTypes: EventType[],
): google.maps.Marker[] => {
  if (!kml) {
    return;
  }

  const { polygons } = kml;
  const points: google.maps.Marker[] = [];
  const numberOfPoints = 20;
  const renderingDistance = 50;

  polygons.forEach((polygon) => {
    if (
      eventTypes?.length > 0 &&
      !eventTypes.includes(polygon.type as EventType) &&
      !eventTypes.includes((polygon.type + '_MATCH') as EventType)
    ) {
      return;
    }

    try {
      const distance = google.maps.geometry.spherical.computeDistanceBetween(polygon.path[0], center);

      if (distance > renderingDistance) {
        return;
      }
    } catch (e) {
      return;
    }

    const pathStart = polygon.path[0];
    const pathEnd = polygon.path[polygon.path.length - 1];

    const x0 = pathStart.lat;
    const y0 = pathStart.lng;
    const x1 = pathEnd.lat;
    const y1 = pathEnd.lng;

    const dx = (x1 - x0) / numberOfPoints;
    const dy = (y1 - y0) / numberOfPoints;

    for (let i = 0; i < numberOfPoints; i++) {
      const x = x0 + dx * i;
      const y = y0 + dy * i;

      const point = new google.maps.Marker({
        position: { lat: x, lng: y },
        map,
        title: 'Enforcement area',
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 2,
          strokeColor: polygon.color,
        },
      });

      points.push(point);
    }
  });

  return points;
};
